import React from "react"
import { Footer } from "./Footer"
import { FormContainer, Worksheet, CheckboxYesNo, LabeledInput } from "uikit"
import { Radio, Grid, Checkbox, Dropdown } from "semantic-ui-react"
import { useStore } from "store"

const PeopleCheckboxGroup = ({ people, coveredPeople, person_id }) => (
  <Grid columns="equal">
    {people.map(person => (
      <Grid.Row textAlign="left" key={person.id} style={{ padding: "0px" }}>
        <Grid.Column>
          <Checkbox
            disabled={person.id === person_id}
            label={person.first_name}
            checked={
              person.id === person_id ||
              !!coveredPeople.array.find(
                element => element.fields.person_id.value === person.id
              )
            }
            onChange={(event, eventData) => {
              if (eventData.checked) {
                coveredPeople.append({ person_id: person.id })
              } else {
                coveredPeople.array
                  .find(element => element.fields.person_id.value === person.id)
                  .remove()
              }
            }}
          />
        </Grid.Column>
      </Grid.Row>
    ))}
  </Grid>
)

const MXHCPlanForm = ({
  data: {
    fields: {
      monthly_premium,
      people_coverage,
      person_id,
      covered_people,
      is_hdhp
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  isNew,
  onDelete,
  ownerOptions,
  people,
  showDescription
}) => {
  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row label="What is the monthly premium?" paddedField>
          <Worksheet.Field error={monthly_premium.error}>
            <LabeledInput
              {...monthly_premium}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Who is the owner of this health plan?"
          paddedField
        >
          <Worksheet.Field error={person_id.error}>
            <Dropdown
              {...person_id}
              placeholder="Person"
              options={ownerOptions}
              selection
              data-testid="incomeEarner"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row label="Coverage of the health plan?" paddedField>
          <Worksheet.Field>
            <div className="WorksheetField__RadioGroup">
              <Radio
                label="Individual"
                name={people_coverage.name}
                checked={people_coverage.value === "individual"}
                onChange={() => {
                  people_coverage.onChange(null, { value: "individual" })
                  covered_people.clear()
                }}
              />
              <br />
              <Radio
                label="Family"
                name={people_coverage.name}
                checked={people_coverage.value === "family"}
                onChange={() => {
                  people_coverage.onChange(null, { value: "family" })
                }}
              />
              <br />
              <Radio
                label="Other"
                name={people_coverage.name}
                checked={people_coverage.value === "other"}
                onChange={() => {
                  people_coverage.onChange(null, { value: "other" })
                }}
              />
            </div>
          </Worksheet.Field>
          {people_coverage.value === "other" ? (
            <Worksheet.Row label="Who is covered by this plan?" paddedField>
              <Worksheet.Field padded>
                <PeopleCheckboxGroup
                  people={people}
                  person_id={person_id.value}
                  coveredPeople={covered_people}
                />
              </Worksheet.Field>
            </Worksheet.Row>
          ) : null}
        </Worksheet.Row>
        <Worksheet.Row
          label="Are you eligible to contribute to a Health Savings Account (HSA) with this plan?"
          description={
            showDescription
              ? "If you are eligible for insurance under a traditional employer-sponsored plan, you might not be eligible to contribute to an HSA through your marketplace/exchange plan. Checking this box confirms that you are eligible."
              : ""
          }
          paddedField
        >
          <Worksheet.Field>
            <CheckboxYesNo fluid {...is_hdhp} />
          </Worksheet.Field>
        </Worksheet.Row>
      </Worksheet.Body>
      <Footer
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Health Plan"
        error={error}
      />
    </Worksheet>
  )
}

const MXHCPlanFormContainer = FormContainer(
  {
    monthly_premium: {
      type: "unit"
    },
    person_id: {
      type: "string"
    },
    people_coverage: {
      type: "string",
      defaultValue: "other"
    },
    covered_people: {
      type: "array",
      schema: {
        person_id: {
          type: "string"
        }
      }
    },
    is_hdhp: {
      type: "boolean",
      defaultValue: "false"
    }
  },
  (fields, ...form) => {}
)(MXHCPlanForm)

const MXHCPlan = ({ benefit, household, type, ...formProps }) => {
  const [people, { addBenefit, updateBenefit, deleteBenefit }] = useStore(
    "householdPeople",
    household.id
  )
  const [benefitsObject] = useStore("householdBenefits", household.id)

  const owners = Object.values(people)
  const benefits = Object.values(benefitsObject)

  const hasSpouse = !!owners.filter(p => p.role === "spouse").length
  const hasEshcPlan = !!benefits.filter(b => b.type === "eshc_plan").length

  const ownerOptions = owners
    .filter(({ role }) => role === "spouse" || role === "client")
    .map(({ id: value, first_name: text }) => ({
      value,
      text
    }))

  const isNew = benefit === null
  const values = isNew ? {} : benefit
  const submit = isNew
    ? async values => {
        const coveredPeople = [...values.covered_people]
        coveredPeople.push({
          person_id: values.person_id
        })
        await addBenefit({
          household_id: household.id,
          ...values,
          covered_people: coveredPeople,
          type
        })
      }
    : async values => {
        const coveredPeople = [...values.covered_people]
        coveredPeople.push({
          person_id: values.person_id
        })
        await updateBenefit({
          id: benefit.id,
          ...values,
          covered_people: coveredPeople,
          type
        })
      }
  const onDelete = () =>
    deleteBenefit({
      id: benefit.id,
      household_id: household.id
    })
  return (
    <MXHCPlanFormContainer
      {...{
        values,
        isNew,
        submit,
        onDelete,
        ownerOptions,
        people: owners,
        showDescription: hasSpouse || hasEshcPlan,
        ...formProps
      }}
    />
  )
}

export default MXHCPlan
